import { useState } from 'react';
import { Loader2Icon, PlusIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAssignScanner } from '@/services/projectBackOffice.service';
import { useBackOfficeScanners } from '@/services/scanner.service';
import ProjectSelect from './ProjectSelect';
import { saveErrorMessage } from './saveError';

const FIELD =
    'bg-[#0f141a] border-[#1e242a] text-[#e5e7e3] placeholder:text-[#6b7480]';
const LABEL =
    'block text-[11px] uppercase tracking-wider text-[#6b7480] mb-1.5';

/**
 * A serial number and a project: a scanner not seen yet is created there, one
 * on another project moves there.
 */
export default function AddScannerDialog() {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button
                size="sm"
                onClick={() => setOpen(true)}
                className="bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
            >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Add scanner to a project
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md bg-[#161c22] border-[#1e242a] text-[#d8dde2]">
                    <DialogHeader>
                        <DialogTitle className="text-white">
                            Add scanner to a project
                        </DialogTitle>
                    </DialogHeader>
                    {/* Remounts each time, so the last entry is gone. */}
                    {open && <AddScannerForm onDone={() => setOpen(false)} />}
                </DialogContent>
            </Dialog>
        </>
    );
}

function AddScannerForm({ onDone }: { onDone: () => void }) {
    const { data: scanners } = useBackOfficeScanners();
    const assign = useAssignScanner();
    const [serial, setSerial] = useState('');
    const [projectId, setProjectId] = useState('');

    const trimmed = serial.trim();
    const already = scanners?.some(
        (s) => s.serialNumber === trimmed && s.projectId === projectId,
    );
    const canSave = !!trimmed && !!projectId && !already && !assign.isPending;

    const save = () => {
        if (!canSave) return;
        assign.mutate(
            { projectId, serialNumber: trimmed },
            { onSuccess: onDone },
        );
    };

    return (
        <div className="space-y-4">
            <div>
                <label className={LABEL}>Serial number</label>
                <Input
                    autoFocus
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && save()}
                    list="add-scanner-known"
                    className={`${FIELD} font-mono`}
                />
                <datalist id="add-scanner-known">
                    {scanners?.map((s) => (
                        <option key={s.serialNumber} value={s.serialNumber}>
                            {s.projectName ?? ''}
                        </option>
                    ))}
                </datalist>
            </div>
            <div>
                <label className={LABEL}>Project</label>
                <ProjectSelect value={projectId} onChange={setProjectId} />
            </div>
            {already && (
                <p className="text-xs text-[#6b7480]">
                    Already on this project.
                </p>
            )}
            {assign.error && (
                <p className="text-sm text-red-400">
                    {saveErrorMessage(assign.error)}
                </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
                <Button
                    variant="ghost"
                    onClick={onDone}
                    className="text-[#a8b0bb] hover:bg-[#1e242a] hover:text-white"
                >
                    Cancel
                </Button>
                <Button
                    onClick={save}
                    disabled={!canSave}
                    className="bg-[#2f6f4e] hover:bg-[#3a865f] text-white"
                >
                    {assign.isPending && (
                        <Loader2Icon className="h-4 w-4 mr-1.5 animate-spin" />
                    )}
                    Save
                </Button>
            </div>
        </div>
    );
}
